import { Link } from 'react-router-dom';
import {
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  HelpCircle,
  Info,
  ShieldCheck,
  Calendar,
  Bell,
  Clock,
  ClipboardList,
  ArrowRight,
  Wand2,
  Settings2,
  Sparkles,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/app/components/ui/tooltip';
import { Card, CardContent } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  useComplianceSummary,
  useUrgentActions,
  useUpcomingDeadlines,
  useRefreshAssessment,
  usePendingProposal,
} from '@/hooks/use-compliance-dashboard';
import { useProposedEvidence } from '@/hooks/use-proposed-evidence';
import { ProposalReviewPanel } from './components/proposal-review-panel';
import { AcceptAllUndoBanner } from './components/accept-all-undo-banner';
import { useSetupWizardProfile } from '@/hooks/use-setup-wizard';
import { OpenNonConformancesCard } from './components/open-non-conformances-card';
import { useOrgToday } from '@/hooks/use-settings';
import { formatDateOnly } from '@/lib/date-utils';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import type {
  AlertSeverity,
  ComplianceAlert,
  ComplianceDeadline,
  ComplianceSectionRollup,
} from '@/lib/types';

// ─── Config ─────────────────────────────────────────────────────────────────

const SEVERITY_CONFIG: Record<AlertSeverity, { color: string; bg: string }> = {
  critical: { color: 'text-red-800', bg: 'bg-red-100' },
  warning: { color: 'text-amber-800', bg: 'bg-amber-100' },
  info: { color: 'text-blue-800', bg: 'bg-blue-100' },
};

function scoreColor(score: number): string {
  if (score >= 80) return 'text-green-600';
  if (score >= 50) return 'text-amber-600';
  return 'text-red-600';
}

function barColor(pct: number): string {
  if (pct >= 80) return 'bg-green-500';
  if (pct >= 50) return 'bg-amber-500';
  return 'bg-red-500';
}

// ─── Main Component ─────────────────────────────────────────────────────────

export function ComplianceLandingPage() {
  const { data: summary, isLoading: summaryLoading } = useComplianceSummary();
  const { data: urgentActions, isLoading: actionsLoading } = useUrgentActions();
  const { data: deadlines, isLoading: deadlinesLoading } = useUpcomingDeadlines();
  const refreshMutation = useRefreshAssessment();
  const { data: pendingProposal } = usePendingProposal();
  const { data: proposedEvidence } = useProposedEvidence();
  const { data: wizardProfile, isLoading: wizardLoading } = useSetupWizardProfile();

  const wizardStatus = wizardProfile?.wizard_status ?? 'not_started';
  const isLoading = summaryLoading || actionsLoading || deadlinesLoading || wizardLoading;

  if (isLoading) {
    return (
      <div className="space-y-6 p-4 sm:p-6 lg:p-8">
        <Skeleton className="h-10 w-72" />
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          <Skeleton className="h-48" />
          <Skeleton className="col-span-1 h-48 lg:col-span-2" />
        </div>
        <Skeleton className="h-64" />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Skeleton className="h-72" />
          <Skeleton className="h-72" />
        </div>
      </div>
    );
  }

  // First-run empty state
  if (summary && !summary.profile_complete) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <ShieldCheck className="h-16 w-16 text-gray-300" />
        <h2 className="mt-4 text-xl font-semibold text-gray-700">
          Complete your compliance profile
        </h2>
        <p className="mt-2 max-w-md text-sm text-gray-500">
          Set up your organisation&apos;s compliance profile to determine which QP-300 clauses
          apply, then run your first assessment.
        </p>
        <div className="mt-6 flex gap-3">
          <Button asChild>
            <Link to="/compliance/setup-wizard">
              Set Up Profile
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <ShieldCheck className="h-16 w-16 text-gray-300" />
        <h2 className="mt-4 text-xl font-semibold text-gray-700">No compliance data</h2>
        <p className="mt-2 text-sm text-gray-500">
          Run an assessment to populate your compliance dashboard.
        </p>
        <Button
          className="mt-6"
          onClick={() => refreshMutation.mutate()}
          disabled={refreshMutation.isPending}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
          Run Assessment
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8">
      {/* Setup Wizard Banner */}
      {wizardStatus !== 'completed' ? (
        <div className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
          <div className="flex items-center gap-3">
            <Wand2 className="h-5 w-5 text-blue-600" />
            <div>
              <p className="text-sm font-semibold text-blue-900">
                {wizardStatus === 'in_progress' ? 'Resume Your Compliance Setup' : 'Complete Your Compliance Setup'}
              </p>
              <p className="text-xs text-blue-700">
                {wizardStatus === 'in_progress'
                  ? 'You have an in-progress setup wizard — pick up where you left off.'
                  : 'Run the setup wizard to configure your compliance profile and generate documents.'}
              </p>
            </div>
          </div>
          <Button asChild size="sm">
            <Link to="/compliance/setup-wizard">
              {wizardStatus === 'in_progress' ? 'Resume Setup' : 'Start Setup'}
              <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </div>
      ) : (
        <div className="flex justify-end">
          <Button asChild variant="ghost" size="sm">
            <Link to="/compliance/setup-wizard">
              <Settings2 className="mr-1.5 h-4 w-4" />
              Edit Setup
            </Link>
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Compliance Overview</h1>
          <p className="mt-1 text-sm text-gray-500">
            QP-300 compliance at a glance
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => refreshMutation.mutate()}
            disabled={refreshMutation.isPending}
            variant="outline"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
            {refreshMutation.isPending ? 'Running...' : 'Run Assessment'}
          </Button>
          <Button asChild variant="outline">
            <Link to="/compliance/audit-prep/new">
              <ClipboardList className="mr-2 h-4 w-4" />
              Start Internal Audit
            </Link>
          </Button>
        </div>
      </div>

      {/* Score card + Status tiles */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <ScoreCard
          score={summary.score}
          compliant={summary.compliant}
          applicable={summary.applicable}
          unevidencedCompliant={summary.unevidenced_compliant ?? 0}
          unprovisioned={summary.unprovisioned ?? 0}
          wizardComplete={wizardStatus === 'completed'}
        />
        <div className="col-span-1 lg:col-span-2">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatusTile
              label="Compliant"
              count={summary.status_counts.compliant}
              icon={CheckCircle2}
              variant="green"
            />
            <StatusTile
              label="In Progress"
              count={summary.status_counts.partially_compliant}
              icon={AlertTriangle}
              variant="amber"
            />
            <StatusTile
              label="Gaps"
              count={summary.status_counts.non_compliant}
              icon={XCircle}
              variant="red"
            />
            <StatusTile
              label="N/A"
              count={summary.status_counts.not_applicable}
              icon={HelpCircle}
              variant="gray"
            />
          </div>
        </div>
      </div>

      {/* Assessment proposal review (S04) */}
      {pendingProposal && (
        <ProposalReviewPanel proposal={pendingProposal} />
      )}
      <AcceptAllUndoBanner />

      {/* Proposed evidence inbox (SQ-2) */}
      {proposedEvidence && proposedEvidence.length > 0 && (
        <Card>
          <CardContent className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <Sparkles className="h-5 w-5 text-amber-500" />
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {proposedEvidence.length} proposed evidence item{proposedEvidence.length !== 1 ? 's' : ''} awaiting review
                </p>
                <p className="text-xs text-gray-500">
                  Auto-harvested evidence linked to your clauses — confirm or dismiss in bulk.
                </p>
              </div>
            </div>
            <Button asChild size="sm">
              <Link to="/compliance/proposed-evidence">
                Review Inbox
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Open non-conformances (reconciled with the assessment engine) */}
      <OpenNonConformancesCard
        count={summary.open_nc_count}
        items={summary.open_non_conformances}
      />

      {/* Section rollup bars */}
      {summary.section_rollups.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Section Compliance</h2>
            <div className="space-y-3">
              {summary.section_rollups.map((rollup) => (
                <SectionBar key={rollup.section} rollup={rollup} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Urgent actions + Upcoming deadlines side by side */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardContent className="pt-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Urgent Actions</h2>
              <Button variant="outline" size="sm" asChild>
                <Link to="/compliance/alerts">View All</Link>
              </Button>
            </div>
            {!urgentActions || urgentActions.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-400">No urgent actions</p>
            ) : (
              <div className="space-y-3">
                {urgentActions.map((alert) => (
                  <UrgentActionRow key={alert.id} alert={alert} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Upcoming Deadlines</h2>
            {!deadlines || deadlines.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-400">No upcoming deadlines</p>
            ) : (
              <div className="space-y-3">
                {deadlines.map((d, idx) => (
                  <DeadlineRow key={idx} deadline={d} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function ScoreCard({
  score,
  compliant,
  applicable,
  unevidencedCompliant,
  unprovisioned,
  wizardComplete,
}: {
  score: number;
  compliant: number;
  applicable: number;
  unevidencedCompliant: number;
  unprovisioned: number;
  wizardComplete: boolean;
}) {
  return (
    <Card>
      <CardContent className="py-6">
        <div className="flex flex-col items-center gap-4">
          {wizardComplete && (
            <div className="flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800">
              <CheckCircle2 className="h-4 w-4" />
              Setup Complete
            </div>
          )}
          <div className="text-center">
            <p className="mb-1 text-xs font-medium uppercase tracking-wider text-gray-500">Compliance Score</p>
            <span className={`text-5xl font-bold ${scoreColor(score)}`}>{score}%</span>
          </div>
          <div className="h-3 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className={`h-full rounded-full transition-all duration-700 ${barColor(score)}`}
              style={{ width: `${score}%` }}
            />
          </div>
          <p className="text-sm text-gray-600">
            <span className="font-semibold">{compliant}</span> of{' '}
            <span className="font-semibold">{applicable}</span> applicable clauses ready
          </p>
          {unevidencedCompliant > 0 && (
            <div className="flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
              {unevidencedCompliant} clause{unevidencedCompliant !== 1 ? 's' : ''} marked
              compliant with no evidence on file
            </div>
          )}
          {unprovisioned > 0 && (
            <div
              className="flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800"
              data-testid="score-unprovisioned"
            >
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
              {unprovisioned} catalog clause{unprovisioned === 1 ? '' : 's'} not yet
              provisioned and excluded from this score
            </div>
          )}
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="bottom">
                The score reflects the recorded clause statuses. The assessment
                engine is advisory and its disagreements are shown in the open
                non-conformances view. Clauses marked compliant without linked
                evidence are flagged, but still count toward the score.
              </TooltipContent>
            </Tooltip>
            <span>Based on recorded clause statuses</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StatusTile({
  label,
  count,
  icon: Icon,
  variant,
}: {
  label: string;
  count: number;
  icon: typeof CheckCircle2;
  variant: 'green' | 'amber' | 'red' | 'gray';
}) {
  const colors: Record<string, { border: string; bg: string; text: string; icon: string }> = {
    green: { border: 'border-green-200', bg: 'bg-green-50', text: 'text-green-700', icon: 'text-green-500' },
    amber: { border: 'border-amber-200', bg: 'bg-amber-50', text: 'text-amber-700', icon: 'text-amber-500' },
    red: { border: 'border-red-200', bg: 'bg-red-50', text: 'text-red-700', icon: 'text-red-500' },
    gray: { border: 'border-gray-200', bg: 'bg-gray-50', text: 'text-gray-500', icon: 'text-gray-400' },
  };
  const c = colors[variant];
  return (
    <div className={`rounded-lg border p-4 ${c.border} ${c.bg}`}>
      <div className="flex items-center gap-2">
        <Icon className={`h-5 w-5 ${c.icon}`} />
        <p className={`text-2xl font-bold ${c.text}`}>{count}</p>
      </div>
      <p className={`mt-1 text-xs font-medium ${c.text}`}>{label}</p>
    </div>
  );
}

function SectionBar({ rollup }: { rollup: ComplianceSectionRollup }) {
  const name = rollup.section_name || `Section ${rollup.section}`;
  return (
    <Link
      to={`/compliance/dashboard?section=${rollup.section}`}
      className="group flex items-center gap-4 rounded-lg px-2 py-2 transition-colors hover:bg-gray-50"
    >
      <span className="w-48 shrink-0 text-sm font-medium text-gray-700 group-hover:text-gray-900">
        {rollup.section}. {name}
      </span>
      <div className="h-3 flex-1 overflow-hidden rounded-full bg-gray-200">
        <div
          className={`h-full rounded-full transition-all duration-500 ${barColor(rollup.percent)}`}
          style={{ width: `${rollup.percent}%` }}
        />
      </div>
      <span className="w-20 shrink-0 text-right text-sm font-medium text-gray-600">
        {rollup.compliant}/{rollup.total} ({rollup.percent}%)
      </span>
      <ArrowRight className="h-4 w-4 shrink-0 text-gray-300 group-hover:text-gray-500" />
    </Link>
  );
}

function UrgentActionRow({ alert }: { alert: ComplianceAlert }) {
  const sev = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info;
  return (
    <div className="flex items-start gap-3 rounded-lg border border-gray-100 px-4 py-3">
      <Bell className={`mt-0.5 h-4 w-4 shrink-0 ${sev.color}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Badge className={`${sev.bg} ${sev.color}`}>{alert.severity}</Badge>
          <span className="truncate text-sm font-medium text-gray-900">{alert.title}</span>
        </div>
        <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">{alert.description}</p>
        {alert.due_date && (
          <p className="mt-1 text-xs text-gray-400">
            <Clock className="mr-1 inline h-3 w-3" />
            Due: {formatDateOnly(alert.due_date)}
          </p>
        )}
      </div>
    </div>
  );
}

function DeadlineRow({ deadline }: { deadline: ComplianceDeadline }) {
  const orgToday = useOrgToday();
  const days = differenceInCalendarDays(
    parseISO(deadline.date),
    parseISO(orgToday)
  );

  let badgeClass = 'bg-green-100 text-green-800';
  let badgeText = `${days}d`;
  if (days < 0) {
    badgeClass = 'bg-red-100 text-red-800';
    badgeText = 'Overdue';
  } else if (days <= 30) {
    badgeClass = 'bg-amber-100 text-amber-800';
  }

  return (
    <div className="flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
      <Calendar className="h-5 w-5 shrink-0 text-gray-400" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900">{deadline.label}</p>
        <p className="text-xs text-gray-500">{formatDateOnly(deadline.date)}</p>
      </div>
      <Badge className={badgeClass}>{badgeText}</Badge>
    </div>
  );
}
